import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { categoriesService } from '../../shared/services/catalog.service';
import {
  providersService,
  type AddProviderServicePayload,
  type UpdateProviderServicePayload,
} from '../../shared/services/providers.service';
import type { ProviderServiceItem } from '../../shared/types';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';

/**
 * Dual-mode dialog used by the dashboard to create OR edit a provider
 * service. When {@code service} is provided, the dialog opens in edit
 * mode: fields are pre-filled, the category selector is read-only (the
 * backend forbids re-categorisation — providers delete + re-add for that),
 * and submit calls PATCH /providers/me/services/{id}. Otherwise it's the
 * original "add" flow.
 */
export function AddServiceDialog({
  open,
  onClose,
  service,
}: {
  open: boolean;
  onClose: () => void;
  /** When set, the dialog edits this service instead of creating a new one. */
  service?: ProviderServiceItem | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { countryCode } = useSelectedCountry();
  const isEdit = Boolean(service);

  const [categoryId, setCategoryId] = useState('');
  const [serviceText, setServiceText] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [priceType, setPriceType] = useState<'FIXED' | 'STARTING_FROM'>('FIXED');
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');

  const categories = useQuery({
    queryKey: ['categories', countryCode],
    queryFn: () => categoriesService.list(countryCode),
  });

  // Reset on open / close. In edit mode, pre-fill from the service row;
  // otherwise wipe to a clean slate.
  useEffect(() => {
    if (!open) {
      setCategoryId('');
      setServiceText('');
      setPrice('');
      setCurrency('EUR');
      setPriceType('FIXED');
      setDuration('');
      setDescription('');
      return;
    }
    if (service) {
      // Pre-fill from the existing row. Category resolution is best-effort —
      // we match by code against the loaded categories list, falling back
      // to the first one once both arrive. The Select stays disabled in
      // edit mode so a temporary mismatch can't be saved.
      setServiceText(service.serviceName ?? '');
      setPrice(
        service.customPriceCents != null
          ? (service.customPriceCents / 100).toString()
          : '',
      );
      setCurrency(service.currency ?? 'EUR');
      setPriceType((service.priceType ?? 'FIXED') as 'FIXED' | 'STARTING_FROM');
      setDuration(service.durationMinutes != null ? String(service.durationMinutes) : '');
      setDescription(service.description ?? '');
    }
  }, [open, service]);

  // Resolve categoryId from the service's categoryCode once the categories
  // list is available. We need the ID, not the code, for the Select.
  useEffect(() => {
    if (!open || !service || !categories.data) return;
    const match = categories.data.find((c) => c.code === service.categoryCode);
    if (match) setCategoryId(match.id);
  }, [open, service, categories.data]);

  const priceCents = useMemo(() => {
    if (!price.trim()) return undefined;
    const n = Number(price.replace(',', '.'));
    if (Number.isNaN(n) || n < 0) return undefined;
    return Math.round(n * 100);
  }, [price]);

  const trimmedName = serviceText.trim();
  const canSubmit = isEdit
    ? trimmedName.length > 0
    : Boolean(categoryId) && trimmedName.length > 0;

  const submit = useMutation({
    mutationFn: async () => {
      if (isEdit && service) {
        // Build a partial update. Compare to the original row so we only
        // send fields that actually changed — keeps the audit log clean
        // and lets the backend's "clear" flags do the right thing for
        // emptied-out optional fields.
        const payload: UpdateProviderServicePayload = {};
        if (trimmedName && trimmedName !== service.serviceName) {
          payload.customName = trimmedName;
        }
        if (priceCents == null && service.customPriceCents != null) {
          payload.clearPrice = true;
        } else if (priceCents != null
          && (priceCents !== service.customPriceCents
            || currency !== service.currency
            || priceType !== service.priceType)) {
          payload.customPriceCents = priceCents;
          payload.currency = currency;
          payload.priceType = priceType;
        }
        const durationN = duration.trim() ? Number(duration) : null;
        if (durationN == null && service.durationMinutes != null) {
          payload.clearDuration = true;
        } else if (durationN != null && durationN !== service.durationMinutes) {
          payload.durationMinutes = durationN;
        }
        if (!description && service.description) {
          payload.clearDescription = true;
        } else if (description && description !== service.description) {
          payload.description = description;
        }
        return providersService.updateService(service.id, payload);
      }
      const payload: AddProviderServicePayload = {
        categoryId,
        customName: trimmedName,
        customPriceCents: priceCents,
        currency: priceCents != null ? currency : undefined,
        priceType: priceCents != null ? priceType : undefined,
        durationMinutes: duration ? Number(duration) : undefined,
        description: description || undefined,
      };
      return providersService.addService(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('common.save'));
      onClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span">
          {isEdit ? t('provider.editService') : t('provider.addService')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isEdit ? t('provider.editServiceHint') : t('provider.addServiceHint')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {/* Category — required on add, locked on edit. */}
          <FormControl fullWidth required>
            <InputLabel id="cat-label">{t('home.category')}</InputLabel>
            <Select
              labelId="cat-label"
              label={t('home.category')}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={isEdit}
            >
              {categories.data?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service name — editable in both modes. */}
          <TextField
            label={t('provider.service')}
            placeholder={t('provider.servicePlaceholder')}
            value={serviceText}
            onChange={(e) => setServiceText(e.target.value)}
            disabled={!isEdit && !categoryId}
            required
            inputProps={{ maxLength: 160 }}
            fullWidth
          />

          {/* Price + currency + duration — wraps on narrow screens. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label={t('provider.price')}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
            <TextField
              label={t('provider.currency')}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              sx={{ width: { xs: '100%', sm: 110 } }}
              disabled={!price}
            />
            <TextField
              label={t('provider.duration')}
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
          </Stack>

          {/* Fixed vs starting-from — disabled until a price is set */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              {t('provider.priceTypeLabel')}
            </Typography>
            <ToggleButtonGroup
              value={priceType}
              exclusive
              size="small"
              onChange={(_e, value) => {
                if (value) setPriceType(value);
              }}
              disabled={!price}
              aria-label={t('provider.priceTypeLabel')}
            >
              <ToggleButton value="FIXED" sx={{ textTransform: 'none' }}>
                {t('provider.priceTypeFixed')}
              </ToggleButton>
              <ToggleButton value="STARTING_FROM" sx={{ textTransform: 'none' }}>
                {t('provider.priceTypeStartingFrom')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            label={t('provider.descriptionLabel')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
